import { Injectable } from '@angular/core';
import { ItemProfile, ItemProperties, ItemResource, Link } from '@ngx-hal/models';
import { PropertiesProfilesPipe, PropertyResourceMetaPipe } from '@ngx-hal/pipes';
import { ItemProfileWebService } from '@ngx-hal/web-services';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ItemProfileRead } from './item-profile.actions';

@State<Map<Link['href'], ItemProfile<ItemResource>>>({
  name: 'itemProfile',
  defaults: null as unknown as undefined,
})
@Injectable()
export class ItemProfileState<Entity extends ItemResource> {
  static read<Entity extends ItemResource>(link: Link) {
    return createSelector([ItemProfileState], (state: Map<Link['href'], ItemProfile<Entity>>) => state?.get(link.href));
  }

  constructor(private readonly itemProfileWebService: ItemProfileWebService<Entity>) {}

  @Action(ItemProfileRead)
  doRead(
    context: StateContext<Map<Link['href'], ItemProfile<Entity>>>,
    { link }: ItemProfileRead,
  ): Observable<Map<Link['href'], ItemProfile<Entity>>> {
    return this.itemProfileWebService.read(link).pipe(
      // sort properties: links first
      map(itemProfile => {
        itemProfile.alps.descriptor.sort(
          (itemPropertyA, itemPropertyB) => (itemPropertyA.rt ? 0 : 1) - (itemPropertyB.rt ? 0 : 1),
        );

        return itemProfile;
      }),

      // check and add parents links if exists
      switchMap(itemProfile => {
        const links = new PropertiesProfilesPipe()
          .transform(itemProfile, undefined, {
            remove: { children: true, technicals: true, functionals: true },
          })
          .map(propertyProfile => ({
            propertyProfile,
            link: new PropertyResourceMetaPipe().transform(propertyProfile)?.link as Link,
          }));

        return links.length
          ? context
              .dispatch(
                links
                  .filter(item => !context.getState()?.has(item.link.href))
                  .map(item => new ItemProfileRead(item.link)),
              )
              .pipe(
                map(() =>
                  links.forEach(item => {
                    const subItemProfile = context.getState()?.get(item.link.href) as ItemProfile<Entity>;

                    new PropertiesProfilesPipe()
                      .transform(subItemProfile, undefined, {
                        remove: { children: true, technicals: true, functionals: true },
                      })
                      .forEach(subPropertyProfile => {
                        const name =
                          `${item.propertyProfile.alps.name}.${subPropertyProfile.alps.name}` as ItemProperties<Entity>;
                        itemProfile.alps.descriptor.splice(
                          itemProfile.alps.descriptor.findIndex(
                            propertyAlps => propertyAlps.name === item.propertyProfile.alps.name,
                          ),
                          0,
                          { ...subPropertyProfile.alps, name },
                        );
                        itemProfile.schema.properties[name] =
                          subItemProfile.schema.properties[subPropertyProfile.alps.name];

                        if (itemProfile.schema.requiredProperties.includes(item.propertyProfile.alps.name)) {
                          itemProfile.schema.requiredProperties.push(name);
                        }
                      });
                  }),
                ),
                map(() => itemProfile),
              )
          : of(itemProfile);
      }),

      // update state
      map(itemProfile =>
        context.setState(new Map(context.getState()?.entries() ?? []).set(itemProfile.alps.href, itemProfile)),
      ),
    );
  }
}
